/**
 * `useArt` — the `Image()` art probe (prototype `art` / `artTry`, lines
 * 1463-1502).
 *
 * `store/selectors.ts` returns an ordered *candidate ladder* (`coverCands` /
 * `hdrCands`, from `lib/logic.ts#artCands`) rather than a resolved background,
 * because resolving it is network I/O and therefore a component concern
 * (PORTING-NOTES P2-01). This hook is that concern: a module-level cache keyed
 * by `game id + kind`, an `Image()` chain that stops at the first `onload`, and
 * a re-render notification.
 *
 * Two differences from the prototype:
 *  - notifications coalesce into one frame (the prototype called
 *    `forceUpdate()` per load — sixty tiles resolving in a burst would be sixty
 *    renders);
 *  - the ladder is re-walked when its signature changes (a Steam asset batch
 *    landing adds rungs), and whatever already resolved stays on screen while
 *    the new walk runs.
 *
 * Every rung is a plain image URL, so the probe never touches the Worker proxy
 * and art resolves even when `/api/steam/*` is down. Passing `hasCover={false}`
 * to `components/CoverArt.tsx` is now only the loading state, not a placeholder.
 *
 * Landed in `screens/Discover.tsx` during Phase 3c because `components/` was
 * frozen for that lane (P3c-01); moved here in the integration pass, which is
 * where its header note said it belonged.
 */

import { useEffect, useReducer, useRef } from 'react';

import type { ArtCand, GameId } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { appidsOf } from '../store/selectors.3c.ts';

export interface ResolvedArt {
  hasCover: boolean;
  /** CSS `background` shorthand — pass straight to `CoverArt.cover` */
  cover: string;
}

interface ArtRec {
  /** the candidate list this record was probed against */
  sig: string;
  url: string;
  mode: 'cover' | 'contain';
  /** a walk is in flight — a remount must not start a second one */
  walking: boolean;
}

/**
 * Prototype `this._artM`: one record per `game id + kind`.
 *
 * NO LONGER "never re-probed", and the difference is a bug Marcus hit on
 * production (2026-08-22): `Image.onerror` fires for a TRANSIENT network
 * failure exactly as it does for a real 404, and a crew switch fires the
 * catalog, the snapshot, the boards and a wall of these probes all at once —
 * the precise moment a mobile browser drops requests. A card whose whole
 * ladder happened to fail in that burst was recorded as walked-and-empty and
 * never tried again, so it sat blank for the rest of the session while its
 * neighbours, whose probes squeaked through, were fine. "Sometimes the card
 * image does not load for some cards", only around crew switches.
 *
 * So an EXHAUSTED walk is a retryable state, not a verdict: `probeArt` walks
 * again on the next mount when the record has no url — and a crew switch
 * remounts every card, which is exactly when the retry is wanted. A resolved
 * record still never re-probes, and a game with genuinely no art re-probes
 * only its few rungs, once per mount, which is the honest price of not being
 * able to tell "gone" from "dropped".
 */
const artCache = new Map<string, ArtRec>();
const artListeners = new Set<() => void>();
let flushQueued = false;

/**
 * Coalesce a burst of `onload`s into one flush. `requestAnimationFrame` is the
 * right primitive in a visible tab — but a BACKGROUND tab never fires it, and
 * an rAF-only flush would leave already-resolved art sitting in the cache
 * unpainted until some unrelated store write happened to re-render. The timer
 * is the backstop; whichever runs first clears `flushQueued` and the other is a
 * no-op. (Verified: `document.hidden === true` ⇒ zero rAF callbacks.)
 */
function notifyArt(): void {
  if (flushQueued) return;
  flushQueued = true;
  const flush = () => {
    if (!flushQueued) return;
    flushQueued = false;
    for (const l of [...artListeners]) l();
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush);
  setTimeout(flush, 32);
}

function artStep(rec: ArtRec, cands: readonly ArtCand[], i: number): void {
  const c = cands[i];
  if (!c || typeof Image === 'undefined') {
    // The ladder is exhausted (or there is no DOM). `walking` drops WITHOUT a
    // url, which is what lets the next mount try again — see `artCache`.
    rec.walking = false;
    return;
  }
  const img = new Image();
  img.onload = () => {
    rec.walking = false;
    if (rec.url === c.u) return;
    rec.url = c.u;
    rec.mode = c.m;
    notifyArt();
  };
  img.onerror = () => artStep(rec, cands, i + 1);
  img.src = c.u;
}

/**
 * Decide whether `key` needs a walk against `cands`, and start one if so.
 * The whole decision, extracted from the hook's effect so it is testable
 * without a renderer:
 *
 *  - same ladder, RESOLVED       → nothing to do, ever;
 *  - same ladder, WALK IN FLIGHT → nothing to do, the walk will finish;
 *  - same ladder, walked & EMPTY → walk again (the transient-failure repair);
 *  - new ladder                  → walk it, keeping whatever already resolved
 *                                  on screen while the new walk runs.
 */
export function probeArt(key: string, cands: readonly ArtCand[]): void {
  const sig = cands.map((c) => c.u).join('|');
  const cur = artCache.get(key);
  if (cur && cur.sig === sig && (cur.url !== '' || cur.walking)) return;
  const rec: ArtRec = {
    sig,
    url: cur ? cur.url : '',
    mode: cur ? cur.mode : 'cover',
    walking: true,
  };
  artCache.set(key, rec);
  artStep(rec, cands, 0);
}

/**
 * Resolve one candidate ladder to a CSS background. `key` is the prototype's
 * `g.id + kind` — `'p'` for the 2/3 portrait capsules, `'w'` for the 460×215
 * header thumbnails — so the two kinds of the same game never share a record.
 */
export function useArt(key: string, cands: readonly ArtCand[]): ResolvedArt {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const sig = cands.map((c) => c.u).join('|');

  useEffect(() => {
    artListeners.add(bump);
    return () => {
      artListeners.delete(bump);
    };
  }, []);

  useEffect(() => {
    probeArt(key, cands);
    // `cands` is fully described by `sig`; depending on the array identity
    // would re-probe every render, because the selector mints a fresh array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, sig]);

  const rec = artCache.get(key);
  const url = rec ? rec.url : '';
  return {
    hasCover: !!url,
    cover: url ? 'url("' + url + '") center/' + (rec ? rec.mode : 'cover') + ' no-repeat' : '',
  };
}

/**
 * THE OTHER HALF OF THE ART STORY: a surface that paints covers must ASK for
 * them. `useArt` above only walks a ladder; `lib/logic.ts#artCands` can only
 * GUESS the legacy CDN shapes for a game whose assets were never fetched, and
 * those 404 for anything recent — a modern appid publishes its capsule at a
 * content-hashed path that exists nowhere but a GetItems response. So a screen
 * that renders a cover without ever reaching `/api/steam/items` for it draws a
 * checkerboard, permanently.
 *
 * That gap has now been closed one screen at a time five times — GOTY, both
 * Discover screens, Playtests, MY SCORES, the backlog shelf — each time as a
 * ref + joined key + effect copied from the last one, and each time only after
 * Marcus found a blank tile in production. This hook is that copy, made once,
 * so the next screen is two lines instead of a re-derivation:
 *
 *     useQueueArt(v.rows.map((r) => r.id));
 *
 * Pass the ids the screen is ACTUALLY RENDERING. The ref-against-a-joined-key
 * shape is load-bearing: selectors mint a fresh array every render, so an
 * effect depending on the array itself would re-queue on every paint. The
 * per-appid `SURFACE_MIN_MS` floor in `revalidateSurface` is what makes a
 * re-queue after a filter keystroke free, so a screen may call this freely.
 *
 * Ids, not appids, because that is what a view-model row carries; `appidsOf`
 * resolves them and drops the rows with no Steam id (a CheapShark row has
 * nothing to look up). A screen holding appids already — `Playtests`, whose
 * `playtestsVM` publishes `appids` — calls `queueAssets` directly.
 *
 * WHAT DOES NOT CALL THIS, and why, so a grep for the gap answers itself:
 *  - BOTH DETAIL SCREENS. `openGame` asks for the opened game store-side, which
 *    covers the sheets and modals that hang off Detail too (RECOMMEND, TELL ME
 *    WHEN, SCORE THIS GAME) — see the note there.
 *  - The board sheets and modals — MOVE, NOMINATE, the drag ghost, WHAT'S NEW.
 *    Every one draws a game the party list already put on screen, and the three
 *    board screens queue that list.
 */
export function useQueueArt(ids: readonly GameId[]): void {
  const idsRef = useRef<readonly GameId[]>(ids);
  idsRef.current = ids;
  const idsKey = ids.join(',');
  useEffect(() => {
    const appids = appidsOf(useQd.getState(), idsRef.current);
    if (appids.length) useQd.getState().queueAssets(appids);
  }, [idsKey]);
}

/** Test seam — the probe cache is module-level, exactly as `_artM` was. */
export function resetArtCache(): void {
  artCache.clear();
}
